// Deterministischer Zufall. Konzept §12.2: "gleiche Koordinate -> gleicher Baum,
// immer". Kein Speichern, automatisch identisch für alle Spieler.
//
// Alle Funktionen sind rein: gleiche Eingabe -> gleiche Ausgabe, auf jeder
// Maschine, in jeder Session.

/** 32-bit Hash (FNV-1a-artig mit Avalanche). */
export function hashInt(...ints) {
  let h = 0x811c9dc5;
  for (let i = 0; i < ints.length; i++) {
    let v = ints[i] | 0;
    h ^= v & 0xff; h = Math.imul(h, 0x01000193);
    h ^= (v >>> 8) & 0xff; h = Math.imul(h, 0x01000193);
    h ^= (v >>> 16) & 0xff; h = Math.imul(h, 0x01000193);
    h ^= (v >>> 24) & 0xff; h = Math.imul(h, 0x01000193);
  }
  // Avalanche, damit benachbarte Koordinaten nicht korrelieren.
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15; h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Hash aus einem String (für Namen, IDs, Seeds). */
export function hashString(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i) & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (str.charCodeAt(i) >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15; h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Deterministisch [0,1) aus beliebig vielen Integers. */
export function rand01(...ints) {
  return hashInt(...ints) / 4294967296;
}

/** Deterministisch [min,max) . */
export function randRange(min, max, ...ints) {
  return min + rand01(...ints) * (max - min);
}

/** Deterministisch ein Element aus einem Array. */
export function pick(array, ...ints) {
  if (array.length === 0) return undefined;
  return array[hashInt(...ints) % array.length];
}

/** Deterministisch true mit Wahrscheinlichkeit p. */
export function chance(p, ...ints) {
  return rand01(...ints) < p;
}

/**
 * Sequenzieller PRNG (mulberry32) für Fälle, in denen ein Stream gebraucht wird
 * (z. B. Zugzusammenstellung). Aus einem Seed erzeugt, damit reproduzierbar.
 */
export function makeRng(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function rngRange(rng, min, max) {
  return min + rng() * (max - min);
}

export function rngInt(rng, min, maxInclusive) {
  return Math.floor(min + rng() * (maxInclusive - min + 1));
}

export function rngPick(rng, array) {
  return array[Math.min(array.length - 1, Math.floor(rng() * array.length))];
}

/** Gewichtete Auswahl: entries = [[wert, gewicht], ...] */
export function rngWeighted(rng, entries) {
  let total = 0;
  for (const [, w] of entries) total += w;
  let r = rng() * total;
  for (const [value, w] of entries) {
    r -= w;
    if (r <= 0) return value;
  }
  return entries[entries.length - 1][0];
}

// --- Value Noise -------------------------------------------------------------

function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

/** 2D Value Noise in [-1,1], deterministisch aus Seed. */
export function noise2(x, y, seed = 0) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = smoothstep(xf), v = smoothstep(yf);

  const n00 = rand01(xi, yi, seed);
  const n10 = rand01(xi + 1, yi, seed);
  const n01 = rand01(xi, yi + 1, seed);
  const n11 = rand01(xi + 1, yi + 1, seed);

  const a = n00 + u * (n10 - n00);
  const b = n01 + u * (n11 - n01);
  return (a + v * (b - a)) * 2 - 1;
}

/** Fraktales Noise (fBm) in etwa [-1,1]. */
export function fbm2(x, y, seed = 0, octaves = 4, lacunarity = 2.0, gain = 0.5) {
  let sum = 0, amp = 1, norm = 0, fx = x, fy = y;
  for (let i = 0; i < octaves; i++) {
    sum += noise2(fx, fy, seed + i * 1013) * amp;
    norm += amp;
    amp *= gain;
    fx *= lacunarity;
    fy *= lacunarity;
  }
  return sum / norm;
}

/** 1D Value Noise in [-1,1] (für Höhenprofile entlang der Strecke). */
export function noise1(x, seed = 0) {
  const xi = Math.floor(x);
  const xf = x - xi;
  const u = smoothstep(xf);
  const a = rand01(xi, seed);
  const b = rand01(xi + 1, seed);
  return (a + u * (b - a)) * 2 - 1;
}

export function fbm1(x, seed = 0, octaves = 4) {
  let sum = 0, amp = 1, norm = 0, fx = x;
  for (let i = 0; i < octaves; i++) {
    sum += noise1(fx, seed + i * 7919) * amp;
    norm += amp;
    amp *= 0.5;
    fx *= 2;
  }
  return sum / norm;
}
